import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  Button,
  Tab,
  TabList,
  Text,
  Checkbox,
  RadioGroup,
  Radio,
  makeStyles,
  CounterBadge,
} from "@fluentui/react-components";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useState, useRef } from "react";
import {
  fetchExtracts,
  selectConditionExtracts,
  selectSelectionExtracts,
} from "../../../redux/savedSearchSlices/extractSlice";
import { AppDispatch } from "../../../redux/store";
import ASCButton from "../../../components/Button";
import { ArrowLeft16Regular } from "@fluentui/react-icons";
import { ExtractMain } from "../../utilities/ExtractLocate/extractMain";
import MainPage from "../SavedSearch/MainPage";
import ValuePrediction from "../SavedSearch/ValuePrediction";
import ContentLoader from "../../../components/Loader/ContentLoader";

enum TabValues {
  Condition = "condition",
  Selection = "selection",
}

type Screen = "type" | "condition" | "selection" | "export" | "valuePrediction";

const useStyles = makeStyles({
  root: { marginTop: "10px" },
  listContainer: {
    maxHeight: "45vh",
    overflowY: "auto",
    marginTop: "10px",
  },
  buttonGroup: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "15px",
  },
});

interface Props {
  open: boolean;
  onClose: () => void;
  onApply: (data: {
    conditionIds: string[];
    selectionIds?: string[];
    selectionId: string | null;
    encodedCondition?: string | null;
  }) => void;


  initialConditionIds: string[];
  initialSelectionId: string | null;
}

const SavedSearchDialog = ({
  open,
  onClose,
  onApply,
  initialConditionIds,
  initialSelectionId,
}: Props) => {
  const styles = useStyles();
  const dispatch = useDispatch<AppDispatch>();

  const conditionExtracts = useSelector(selectConditionExtracts);
  const selectionExtracts = useSelector(selectSelectionExtracts);

  const [tab, setTab] = useState<TabValues>(TabValues.Condition);
  const [screen, setScreen] = useState<
    "type" | "condition" | "selection" | "valuePrediction"
  >("type");

  const [selectedConditionItem, setSelectedConditionItem] = useState<any>(null);

  const [encodedCondition, setEncodedCondition] = useState<string | null>(null);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedRadio, setSelectedRadio] = useState<string>("");
  const conditionCount = selectedIds.length;
  const selectionCount = selectedRadio ? 1 : 0;

  const prevOpenRef = useRef(open);

  // -----------------------------------
  // RESET WHEN DIALOG OPENS
  // -----------------------------------
  useEffect(() => {
    // Only trigger initialization if the dialog is transitioning from closed to open
    if (open && !prevOpenRef.current) {

      setScreen("type");

      // Restore tab based on previous selection
      if (initialSelectionId) {
        setTab(TabValues.Selection);
        setSelectedRadio(initialSelectionId || "");
        setSelectedIds([]);
      } else {
        setTab(TabValues.Condition);
        setSelectedIds(initialConditionIds || []);
        setSelectedRadio("");
      }
    }

    if (!open && prevOpenRef.current) {
      // Reset everything when dialog closes
      setScreen("type");
      setSelectedIds([]);
      setSelectedRadio("");
    }

    // Update the ref for the next render
    prevOpenRef.current = open;
  }, [open, initialConditionIds, initialSelectionId]); //  Safe dependencies

  useEffect(() => {
    if (open && conditionExtracts.length === 0 && selectionExtracts.length === 0) {
      dispatch(fetchExtracts());
    }
  }, [open, dispatch]); //  Won't trigger on length changes

  const activeExtracts = useMemo(
    () => (tab === TabValues.Condition ? conditionExtracts : selectionExtracts),
    [tab, conditionExtracts, selectionExtracts],
  );

  const sortedExtracts = useMemo(() => {
    if (tab === TabValues.Condition) {
      return [
        ...activeExtracts.filter((item: any) => selectedIds.includes(item.SearchId)),
        ...activeExtracts.filter(
          (item: any) => !selectedIds.includes(item.SearchId),
        ),
      ];
    }

    if (tab === TabValues.Selection) {
      return [
        ...activeExtracts.filter((item: any) => item.SearchId === selectedRadio),
        ...activeExtracts.filter((item: any) => item.SearchId !== selectedRadio),
      ];
    }

    return activeExtracts;
  }, [activeExtracts, selectedIds, selectedRadio, tab]);

  const handleSelect = () => {
    if (tab === TabValues.Condition) {
      if (selectedIds.length === 0) return;

      setSelectedRadio(""); // clear selection mode

      onApply({
        conditionIds: selectedIds,
        selectionId: null,
        encodedCondition,
      });

      onClose();
    }

    if (tab === TabValues.Selection) {
      if (!selectedRadio) return;

      setSelectedIds([]); // clear condition mode

      const selectedItem = selectionExtracts.find(
        (item: any) => item.SearchId === selectedRadio,
      );

      let selectionIds: string[] = [];

      if (selectedItem?.Condition) {
        try {
          selectionIds = JSON.parse(selectedItem.Condition);
        } catch (error) {
          console.error("Invalid JSON in Condition");
        }
      }

      onApply({
        conditionIds: [],
        selectionIds,
        selectionId: selectedRadio,
        encodedCondition: null,
      });

      onClose();
    }
  };

  // -----------------------------------
  // TYPE SCREEN
  // -----------------------------------
  const renderTypeScreen = () => (
    <>
      <DialogTitle>Extract Type</DialogTitle>

      <DialogContent>
        <Text>Choose which type of extract you want to create</Text>
        <TabList
          selectedValue={tab}
          onTabSelect={(_, data) => {
            const newTab = data.value as TabValues;
            setTab(newTab);

            if (newTab === TabValues.Condition) {
              setSelectedRadio("");
            } else {
              setSelectedIds([]);
            }
          }}
          className={styles.root}
        >
          <Tab value={TabValues.Condition}>
            Extract by Condition{" "}
            {conditionCount > 0 &&

              <CounterBadge count={conditionCount} shape="circular" />
            }
          </Tab>

          <Tab value={TabValues.Selection}>
            Extract by Selection{" "}
            {selectionCount > 0 &&
              <CounterBadge count={selectionCount} shape="circular" />
            }
          </Tab>
        </TabList>


        {sortedExtracts.length > 0 ? (
          <>
            <div className={styles.listContainer}>
              {tab === TabValues.Condition ? (
                sortedExtracts.map((item: any) => (
                  <div
                    key={item.SearchId}
                    style={{ display: "flex", gap: "10px" }}
                  >
                    <Checkbox
                      key={item.SearchId}
                      checked={selectedIds.includes(item.SearchId)}
                      onChange={() =>
                        setSelectedIds((prev) =>
                          prev.includes(item.SearchId)
                            ? prev.filter((id) => id !== item.SearchId)
                            : [...prev, item.SearchId],
                        )
                      }
                      label={item.Title}
                    />
                  </div>
                ))
              ) : (
                <RadioGroup
                  value={selectedRadio}
                  onChange={(_, data) => setSelectedRadio(data.value)}
                >
                  {sortedExtracts.map((item: any) => (
                    <Radio
                      key={item.SearchId}
                      value={item.SearchId}
                      label={item.Title}
                    />
                  ))}
                </RadioGroup>
              )}
            </div>


          </>
        ) : (

          <>
            <div style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "50vh",
              color: "#f00",
              fontWeight: "bold",
            }}>There are no active records - click Add / View {tab.charAt(0).toUpperCase() + tab.slice(1)} to create a new record</div>


          </>
        )}
        <div className={styles.buttonGroup}>
          {tab === TabValues.Condition ? (
            <ASCButton
              appearance="primary"
              className="asc-button-primary"
              shape="rounded"
              label="Add / View Condition"
              onClick={() => { setScreen("condition"); }}
            />
          ) : (
            <ASCButton
              appearance="primary"
              className="asc-button-primary"
              shape="rounded"
              label="Add / View Selection"
              onClick={() => setScreen("selection")}
            />
          )}

          <div style={{ display: "flex", gap: 10 }}>
            <ASCButton
              appearance="secondary"
              shape="rounded"
              onClick={onClose}
              label={"Cancel"}
            />
            {sortedExtracts.length > 0 &&
              <ASCButton
                appearance="primary"
                className="asc-button-primary"
                shape="rounded"
                disabled={
                  tab === TabValues.Condition
                    ? selectedIds.length === 0
                    : !selectedRadio
                }
                onClick={handleSelect}
                label="Select"

              />}
          </div>
        </div>


      </DialogContent>
    </>
  );

  // -----------------------------------
  // CONDITION SCREEN
  // -----------------------------------
  const renderConditionScreen = () => {
    return (
      <>
        <DialogTitle style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Button
            shape="circular"
            icon={<ArrowLeft16Regular />}
            onClick={() => setScreen("type")}
          />
          Extract By Condition
        </DialogTitle>

        <DialogContent style={{ padding: 0, height: '60vh', overflow: 'auto' }}>
          <ExtractMain
            extract={conditionExtracts}
            setExtract={(data) => {
              dispatch(fetchExtracts());
            }}
            getExtracts={() => dispatch(fetchExtracts())}
            setIsDialogOpen={() => setScreen("type")}
            setTabOpen={() => setScreen("type")}
            appliedId={(id) => {
              onApply({
                conditionIds: [id],
                selectionId: null,
                encodedCondition: null,
              });
              onClose();
            }}
          />
        </DialogContent>
      </>
    );
  };
  const renderSelectionScreen = () => (
    <>
      <DialogTitle style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <Button
          shape="circular"
          icon={<ArrowLeft16Regular />}
          onClick={() => setScreen("type")}
        />
        Extract Specifications
      </DialogTitle>

      <DialogContent style={{ height: '70vh' }}>
        <MainPage
          isOpen={true}
          onClose={() => setScreen("type")}      // ✅ Go back to type screen
          setTabOpen={() => setScreen("type")}   // ✅ Go back to type screen
          setSelectedTab={(tab) => {
            setScreen("type");                   // ✅ Go back to type screen
          }}
        />
      </DialogContent>
    </>
  );
  const renderValuePrediction = () => (
    <>
      <DialogTitle>Enter Missing Values</DialogTitle>

      <DialogContent>
        <ValuePrediction
          selectedItem={selectedConditionItem}
          onCancel={() => setScreen("type")}
          onApply={(encoded) => {
            setEncodedCondition(encoded);
            onClose();
          }}
        />
      </DialogContent>
    </>
  );

  return (
    <Dialog open={open} modalType="modal">
      <DialogSurface style={{ maxWidth: "80vw" }}>
        <DialogBody>
          {screen === "type" && renderTypeScreen()}
          {screen === "condition" && renderConditionScreen()}
          {screen === "selection" && renderSelectionScreen()}
          {screen === "valuePrediction" && renderValuePrediction()}
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default SavedSearchDialog;
